import React, { useState, useEffect, useMemo, useRef } from 'react';
import { number, func, bool } from 'prop-types';
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage, useForm } from '@laramie/ui';
import { APIS } from '../../../config/apis';
import { ServerRequest } from '../../../utils/apiweb';
import { DatePickerCustom, FormError, FormLabel, InputEjercicio, Loading, Modal, Select, CheckboxButtonGroup, CheckboxButton } from '../../common';
import { useLista } from '../../hooks/useLista';
import { useEntidad } from '../../hooks/useEntidad';
import { useFormErrors } from '../../hooks/useFormErrors';
import { GetMeses, GetFieldMes } from '../../../utils/helpers';
import { getDateNow } from '../../../utils/convert';
import { TIPO_DIA_CALENDARIO } from '../../../consts/tipoDiaCalendario';
import { PERIODICIDAD_ANUAL, PERIODICIDAD_DECLARACION_JURADA } from '../../../consts/periodicidadDeclaracionJurada';
import { VALIDATION_MESSAGE } from '../../../consts/validationMessage';

const PERIODOS_DDJJ = {
    [PERIODICIDAD_DECLARACION_JURADA.BIMESTRAL]: { nombre: 'Bimestre', cantidad: 6 },
    [PERIODICIDAD_DECLARACION_JURADA.TRIMESTRAL]: { nombre: 'Trimestre', cantidad: 4 },
    [PERIODICIDAD_DECLARACION_JURADA.SEMESTRAL]: { nombre: 'Semestre', cantidad: 2 }
};

const CalendarioModal = (props) => {

    const entityInit = {
        id: 0,
        idTipoDiaCalendario: 0,
        fecha: null,
        descripcion: '',
        laborable: false,
        idModeloDeclaracionJurada: null,
        anioDeclaracion: 0,
        periodoDeclaracion: 0
    };

    const [state, setState] = useState({
        loading: false,
        entity: entityInit,
        list: [],
        listCargada: false
    });

    const descripcionManual = useRef(false);

    const [ formValues, formHandle, , formSet ] = useForm({
        idTipoDiaCalendario: 0,
        fecha: null,
        descripcion: '',
        laborable: false,
        idModeloDeclaracionJurada: 0,
        anioDeclaracion: getDateNow().getFullYear().toString(),
        periodoDeclaracion: 0
    });

    const [getListLista, getRowLista, listaReady] = useLista({
        listas: ['TipoDiaCalendario'],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
        },
        memo: { key: 'TipoDiaCalendario', timeout: 0 }
    });

    const [getListEntidad, getRowEntidad, entidadReady] = useEntidad({
        entidades: ['ModeloDeclaracionJurada'],
        onLoaded: (entidades, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
        },
        memo: { key: 'ModeloDeclaracionJurada', timeout: 0 }
    });

    const mount = () => {
        FindCalendario();
        if (props.id > 0) FindCalendarioById();
    }
    useEffect(mount, []);

    const esVencimientoDdjj = Number(formValues.idTipoDiaCalendario) === TIPO_DIA_CALENDARIO.VENCIMIENTOS_DDJJ;
    const esHabilitacionDdjj = Number(formValues.idTipoDiaCalendario) === TIPO_DIA_CALENDARIO.HABILITACION_DDJJ;
    const esPeriodoDdjj = esVencimientoDdjj || esHabilitacionDdjj;
    const esRetencion = Number(formValues.idTipoDiaCalendario) === TIPO_DIA_CALENDARIO.RETENCIONES;
    const laborableFijo = esPeriodoDdjj || esRetencion;

    const periodo = PERIODOS_DDJJ[getRowEntidad('ModeloDeclaracionJurada', Number(formValues.idModeloDeclaracionJurada))?.idPeriodicidadDeclaracionJurada];
    const opcionesPeriodo = (periodo)
        ? Array.from({ length: periodo.cantidad }, (_, index) => ({ value: index + 1, label: `${index + 1}° ${periodo.nombre}` }))
        : GetMeses().map(item => ({value: item.key, label: item.name}));

    const optionsTipoDia = useMemo(() => {
        const list = getListLista('TipoDiaCalendario') ?? [];
        return list
            .filter(item => item.id !== TIPO_DIA_CALENDARIO.FERIADO_NACIONAL)
            .map(item => ({ value: item.id, label: item.nombre }));
    }, [listaReady]);

    const optionsModelo = useMemo(() => {
        const list = getListEntidad('ModeloDeclaracionJurada') ?? [];
        return list
            .filter(item =>
                item.id === state.entity.idModeloDeclaracionJurada ||
                (item.activo && item.idPeriodicidadDeclaracionJurada !== PERIODICIDAD_ANUAL)
            )
            .map(item => ({ value: item.id, label: item.nombre }));
    }, [entidadReady, state.entity.idModeloDeclaracionJurada]);

    const getErrors = () => {
        const errors = {};
        if (Number(formValues.idTipoDiaCalendario) === 0) errors.idTipoDiaCalendario = VALIDATION_MESSAGE.REQUIRED_OPTION;
        if (!formValues.fecha) errors.fecha = VALIDATION_MESSAGE.REQUIRED_DATE;
        if (formValues.descripcion.length === 0) errors.descripcion = VALIDATION_MESSAGE.REQUIRED;
        if (esPeriodoDdjj) {
            const modelo = getRowEntidad('ModeloDeclaracionJurada', Number(formValues.idModeloDeclaracionJurada));
            const duplicado = state.list.some(row =>
                row.id !== props.id &&
                Number(row.idTipoDiaCalendario) === Number(formValues.idTipoDiaCalendario) &&
                Number(row.idModeloDeclaracionJurada) === Number(formValues.idModeloDeclaracionJurada) &&
                Number(row.anioDeclaracion) === Number(formValues.anioDeclaracion) &&
                Number(row.periodoDeclaracion) === Number(formValues.periodoDeclaracion)
            );
            if (!modelo) errors.idModeloDeclaracionJurada = VALIDATION_MESSAGE.REQUIRED_OPTION;
            else if (modelo.idPeriodicidadDeclaracionJurada === PERIODICIDAD_ANUAL) errors.idModeloDeclaracionJurada = 'Los anuales aún no se cargan';
            else if (duplicado) errors.idModeloDeclaracionJurada = 'Ya existe para este período';
            if (formValues.anioDeclaracion.length === 0) errors.anioDeclaracion = VALIDATION_MESSAGE.REQUIRED_SHORT;
            if (Number(formValues.periodoDeclaracion) === 0) errors.periodoDeclaracion = VALIDATION_MESSAGE.REQUIRED_SHORT;
        }
        return errors;
    };

    const getFormatErrors = () => {
        const errors = {};
        if (props.disabled || !esPeriodoDdjj) return errors;
        if (formValues.anioDeclaracion.length > 0 && formValues.anioDeclaracion.length !== 4) errors.anioDeclaracion = 'Ingresá 4 dígitos';
        return errors;
    };

    const { errorOf, validate } = useFormErrors(getErrors, getFormatErrors);

    useEffect(() => {
        if (props.id > 0 || descripcionManual.current || !esPeriodoDdjj) return;
        const modelo = getRowEntidad('ModeloDeclaracionJurada', Number(formValues.idModeloDeclaracionJurada));
        if (!modelo || !formValues.anioDeclaracion) return;
        const periodoDescripcion = (!formValues.periodoDeclaracion)
            ? formValues.anioDeclaracion
            : (periodo)
                ? `${Number(formValues.periodoDeclaracion)}° ${periodo.nombre} ${formValues.anioDeclaracion}`
                : `${GetFieldMes(Number(formValues.periodoDeclaracion), 'cuota')}/${formValues.anioDeclaracion}`;
        formSet({ ...formValues, descripcion: `${esVencimientoDdjj ? 'Vencimiento' : 'Habilitación'} DDJJ ${modelo.nombre} ${periodoDescripcion}` });
    }, [formValues.idModeloDeclaracionJurada, formValues.anioDeclaracion, formValues.periodoDeclaracion, formValues.idTipoDiaCalendario, entidadReady]);

    const handleClickAceptar = () => {
        if (!validate()) return;
        if (esPeriodoDdjj && !state.listCargada) {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'No se pudo verificar si la fecha ya existe, vuelva a abrir el formulario');
            return;
        }
        if (props.id === 0) AddCalendario();
        else ModifyCalendario();
    };

    const handleClickCancelar = () => {
        props.onDismiss();
    }

    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
            setState(prevState => ({...prevState, loading: false}));
        })
        .catch((error) => {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
            setState(prevState => ({...prevState, loading: false}));
        });
    }

    const callbackError = (error) => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud: ' + error.message);
        setState(prevState => ({...prevState, loading: false}));
    }

    function FindCalendario() {
        const callbackSuccess = (response) => {
            response.json()
            .then((data) => {
                setState(prevState => ({...prevState, list: data.filter(x => x.idTipoDiaCalendario === TIPO_DIA_CALENDARIO.VENCIMIENTOS_DDJJ || x.idTipoDiaCalendario === TIPO_DIA_CALENDARIO.HABILITACION_DDJJ), listCargada: true}));
            })
            .catch((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
            });
        };

        ServerRequest(
            REQUEST_METHOD.GET, null, true, APIS.URLS.CALENDARIO, null, null,
            callbackSuccess, callbackNoSuccess, callbackError
        );
    }

    function FindCalendarioById() {
        setState(prevState => ({...prevState, loading: true}));

        const callbackSuccess = (response) => {
            response.json()
            .then((data) => {
                setState(prevState => ({...prevState, loading: false, entity: data}));
                formSet({
                    idTipoDiaCalendario: data.idTipoDiaCalendario,
                    fecha: data.fecha ? new Date(data.fecha) : null,
                    descripcion: data.descripcion,
                    laborable: data.laborable,
                    idModeloDeclaracionJurada: data.idModeloDeclaracionJurada ?? 0,
                    anioDeclaracion: data.anioDeclaracion ? data.anioDeclaracion.toString() : '',
                    periodoDeclaracion: data.periodoDeclaracion ?? 0
                });
            })
            .catch((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
                setState(prevState => ({...prevState, loading: false}));
            });
        };

        ServerRequest(
            REQUEST_METHOD.GET, null, true, APIS.URLS.CALENDARIO, `/${props.id}`, null,
            callbackSuccess, callbackNoSuccess, callbackError
        );
    }

    function AddCalendario() {
        SaveCalendario(REQUEST_METHOD.POST, null);
    }

    function ModifyCalendario() {
        SaveCalendario(REQUEST_METHOD.PUT, `/${props.id}`);
    }

    function SaveCalendario(method, paramsUrl) {
        setState(prevState => ({...prevState, loading: true}));

        const callbackSuccess = (response) => {
            response.json()
            .then(() => {
                setState(prevState => ({...prevState, loading: false}));
                props.onConfirm();
            })
            .catch((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
                setState(prevState => ({...prevState, loading: false}));
            });
        };

        const dataBody = {
            ...state.entity,
            idTipoDiaCalendario: Number(formValues.idTipoDiaCalendario),
            fecha: formValues.fecha,
            descripcion: formValues.descripcion,
            laborable: laborableFijo ? true : formValues.laborable,
            idModeloDeclaracionJurada: esPeriodoDdjj ? Number(formValues.idModeloDeclaracionJurada) : null,
            anioDeclaracion: esPeriodoDdjj ? Number(formValues.anioDeclaracion) : 0,
            periodoDeclaracion: esPeriodoDdjj ? Number(formValues.periodoDeclaracion) : 0
        };

        ServerRequest(
            method, null, true, APIS.URLS.CALENDARIO, paramsUrl, dataBody,
            callbackSuccess, callbackNoSuccess, callbackError
        );
    }

    return (
        <>
        <Loading visible={state.loading}></Loading>

        <Modal
            size="lg"
            title={`Día de calendario: ${(props.id > 0) ? getRowLista('TipoDiaCalendario', state.entity.idTipoDiaCalendario)?.nombre ?? '' : 'Nuevo'}`}
            onEscape={handleClickCancelar}
            body={(
                <div className="row">

                    <div className="col-12 col-md-6">
                        <FormLabel htmlFor="idTipoDiaCalendario" required>Tipo de día</FormLabel>
                        <Select
                            name="idTipoDiaCalendario"
                            className={`form-control ${errorOf('idTipoDiaCalendario') ? 'is-invalid' : ''}`}
                            value={formValues.idTipoDiaCalendario}
                            options={optionsTipoDia}
                            onChange={formHandle}
                            disabled={props.disabled}
                        />
                        <FormError message={errorOf('idTipoDiaCalendario')} />
                    </div>

                    <div className="col-12 col-md-6">
                        <FormLabel htmlFor="fecha" required>{esVencimientoDdjj ? 'Vencimiento de presentación' : esHabilitacionDdjj ? 'Habilitación de presentación' : 'Fecha'}</FormLabel>
                        <DatePickerCustom
                            name="fecha"
                            className={`form-control ${errorOf('fecha') ? 'is-invalid' : ''}`}
                            value={formValues.fecha}
                            onChange={formHandle}
                            disabled={props.disabled}
                        />
                        <FormError message={errorOf('fecha')} />
                    </div>

                    {esPeriodoDdjj && (<>
                        <div className="col-12 col-md-6">
                            <FormLabel htmlFor="idModeloDeclaracionJurada" required>Modelo</FormLabel>
                            <Select
                                name="idModeloDeclaracionJurada"
                                className={`form-control ${errorOf('idModeloDeclaracionJurada') ? 'is-invalid' : ''}`}
                                value={formValues.idModeloDeclaracionJurada}
                                options={optionsModelo}
                                onChange={({target}) => {
                                    formSet({...formValues, idModeloDeclaracionJurada: target.value, periodoDeclaracion: 0});
                                }}
                                disabled={props.disabled}
                            />
                            <FormError message={errorOf('idModeloDeclaracionJurada')} />
                        </div>

                        <div className="col-6 col-md-3">
                            <FormLabel htmlFor="anioDeclaracion" required>Año DDJJ</FormLabel>
                            <InputEjercicio
                                name="anioDeclaracion"
                                className={`form-control ${errorOf('anioDeclaracion') ? 'is-invalid' : ''}`}
                                value={formValues.anioDeclaracion}
                                onChange={formHandle}
                                disabled={props.disabled}
                            />
                            <FormError message={errorOf('anioDeclaracion')} />
                        </div>

                        <div className="col-6 col-md-3">
                            <FormLabel htmlFor="periodoDeclaracion" required>{(periodo) ? `${periodo.nombre} DDJJ` : 'Mes DDJJ'}</FormLabel>
                            <Select
                                name="periodoDeclaracion"
                                className={`form-control ${errorOf('periodoDeclaracion') ? 'is-invalid' : ''}`}
                                value={formValues.periodoDeclaracion}
                                options={opcionesPeriodo}
                                onChange={formHandle}
                                disabled={props.disabled}
                            />
                            <FormError message={errorOf('periodoDeclaracion')} />
                        </div>
                    </>)}

                    <div className="col-12">
                        <FormLabel htmlFor="descripcion" required>Descripción</FormLabel>
                        <input
                            name="descripcion"
                            type="text"
                            placeholder=""
                            className={`form-control ${errorOf('descripcion') ? 'is-invalid' : ''}`}
                            value={formValues.descripcion}
                            onChange={(event) => { descripcionManual.current = true; formHandle(event); }}
                            disabled={props.disabled}
                            maxLength={100}
                        />
                        <FormError message={errorOf('descripcion')} />
                    </div>

                    {!laborableFijo && (
                        <div className="col-12">
                            <CheckboxButtonGroup label="Día laborable">
                                <CheckboxButton
                                    name="laborable"
                                    label="Es laborable"
                                    checked={formValues.laborable}
                                    onChange={formHandle}
                                    disabled={props.disabled}
                                />
                            </CheckboxButtonGroup>
                        </div>
                    )}

                </div>
            )}
            footer={!props.disabled ? (<>
                <button className="btn btn-outline-primary" data-dismiss="modal" onClick={handleClickCancelar}>Cancelar</button>
                <button className="btn btn-primary" data-dismiss="modal" onClick={handleClickAceptar} disabled={state.loading}>Aceptar</button>
            </>) : (
                <button className="btn back-button" data-dismiss="modal" onClick={handleClickCancelar}>Volver</button>
            )}
            footerClassName={props.disabled ? 'f-align' : ''}
        />
        </>
    );
}

CalendarioModal.propTypes = {
    disabled: bool,
    id: number.isRequired,
    onConfirm: func.isRequired,
    onDismiss: func.isRequired,
};

CalendarioModal.defaultProps = {
    disabled: false
};

export default CalendarioModal;
